import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LogOut,
  Menu,
  ShoppingBag,
  User as UserIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { loc } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  featureEnabled,
  useStorefront,
  useStorefrontConfig,
} from "@/lib/storefront";
import { usePresence } from "@/hooks/use-presence";
import { mainMenuDestination } from "@/lib/menu-navigation";

export function Header() {
  const { t, lang, setLang } = useI18n();
  const bootstrap = useStorefront();
  const { isAuthenticated, openLogin, logout } = useAuth();
  const { cart, lineCount } = useCart();
  const config = useStorefrontConfig();
  const [open, setOpen] = useState(false);
  const menuPresence = usePresence(open, 150);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const accountsAvailable =
    featureEnabled(bootstrap, "customer_accounts") &&
    featureEnabled(bootstrap, "phone_verification");
  const navigation = [
    { to: "/" as const, key: "nav.home" as const },
    { to: "/branches" as const, key: "nav.branches" as const },
    {
      to: mainMenuDestination(bootstrap, cart.orderType?.type),
      key: "nav.menu" as const,
    },
  ];
  const hasLanguageChoice =
    bootstrap.tenant.enabledLocales.includes("ar") &&
    bootstrap.tenant.enabledLocales.includes("en");

  const languageChoices = (mobile: boolean) => (
    <div
      role="group"
      aria-label={t("nav.language")}
      dir="ltr"
      className={cn(
        "inline-flex items-center rounded-full border border-border bg-card p-0.5",
        mobile ? "w-full" : "",
      )}
    >
      {(["en", "ar"] as const).map((code) => (
        <button
          key={code}
          type="button"
          aria-label={t(code === "en" ? "nav.languageEnglish" : "nav.languageArabic")}
          aria-pressed={lang === code}
          onClick={() => {
            setLang(code);
            if (mobile) setOpen(false);
          }}
          className={cn(
            "min-h-10 rounded-full px-3 text-xs font-extrabold tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            mobile ? "flex-1" : "min-w-11",
            lang === code
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const brand = loc(config.title, lang);

  const cartButton = (
    <Link
      to="/cart"
      aria-label={`${t("nav.cart")} (${lineCount})`}
      className="pressable relative hidden h-11 items-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground shadow-soft transition hover:brightness-110 md:inline-flex"
    >
      <ShoppingBag className="size-4" aria-hidden="true" />
      <span className="hidden sm:inline">{t("nav.cart")}</span>
      <span className="grid min-w-6 place-items-center rounded-full bg-gold px-1.5 text-xs font-bold text-gold-foreground tabular-nums">
        {lineCount}
      </span>
    </Link>
  );

  const accountControl =
    bootstrap.mode === "preview" ||
    !accountsAvailable ? null : isAuthenticated ? (
      <DropdownMenu dir={lang === "ar" ? "rtl" : "ltr"}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="size-11"
            aria-label={t("nav.account")}
          >
            <UserIcon className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-2xl">
          <DropdownMenuItem asChild>
            <Link to="/account">{t("nav.account")}</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/orders">{t("nav.orders")}</Link>
          </DropdownMenuItem>
          {featureEnabled(bootstrap, "loyalty") ? (
            <DropdownMenuItem asChild>
              <Link to="/loyalty">{t("nav.loyalty")}</Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={logout}>
            <LogOut className="size-4" aria-hidden="true" />
            {t("nav.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <Button
        variant="outline"
        size="icon"
        className="size-11"
        aria-label={t("nav.signin")}
        onClick={() => openLogin()}
      >
        <UserIcon className="size-4" aria-hidden="true" />
      </Button>
    );

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label={brand}>
          {config.logo ? (
            <ResponsiveImage
              source={config.logo}
              sizes="40px"
              alt={brand}
              loading="eager"
              className="h-10 w-auto"
              width={40}
              height={40}
            />
          ) : (
            <span className="grid size-10 place-items-center rounded-2xl gradient-plum text-lg font-black text-primary-foreground">
              {brand.slice(0, 1).toUpperCase() || "E"}
            </span>
          )}
          <span className="hidden text-base font-extrabold leading-tight sm:block">
            {brand}
          </span>
        </Link>

        <nav
          className="mx-auto hidden items-center gap-1 md:flex"
          aria-label={t("nav.main")}
        >
          {navigation.map((n, index) => (
            <Fragment key={n.to}>
              {index > 0 ? (
                <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
              ) : null}
              <Link
                to={n.to}
                activeOptions={{ exact: n.to === "/" }}
                className="rounded-full px-4 py-2 text-sm font-bold text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                activeProps={{ className: "bg-accent text-accent-foreground" }}
              >
                {t(n.key)}
              </Link>
            </Fragment>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2 md:ms-0">
          {hasLanguageChoice ? (
            <div className="hidden md:block">{languageChoices(false)}</div>
          ) : null}
          {cartButton}
          {accountControl}
          <Button
            variant="outline"
            size="icon"
            className="size-11 md:hidden"
            ref={triggerRef}
            aria-controls="mobile-navigation"
            aria-expanded={open}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-4" /> : <Menu className="size-4" />}
          </Button>
        </div>
      </div>

      {menuPresence.rendered ? (
        <div
          ref={panelRef}
          id="mobile-navigation"
          className="motion-mobile-menu border-t border-border bg-card px-4 py-3 shadow-soft md:hidden"
          data-presence={menuPresence.phase}
          aria-hidden={menuPresence.phase === "exiting"}
          inert={menuPresence.phase === "exiting"}
        >
          <nav className="grid gap-1" aria-label={t("nav.main")}>
            {navigation.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                activeOptions={{ exact: n.to === "/" }}
                onClick={() => setOpen(false)}
                className={cn(
                  "rounded-2xl px-4 py-3 text-base font-bold text-foreground hover:bg-accent",
                )}
                activeProps={{ className: "bg-accent text-accent-foreground" }}
              >
                {t(n.key)}
              </Link>
            ))}
            <Link
              to="/cart"
              onClick={() => setOpen(false)}
              className="rounded-2xl px-4 py-3 text-base font-bold hover:bg-accent"
            >
              {t("nav.cart")} ({lineCount})
            </Link>
            {hasLanguageChoice ? (
              <div className="mt-2 border-t border-border pt-3">{languageChoices(true)}</div>
            ) : null}
          </nav>
        </div>
      ) : null}
    </header>
  );
}
